import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import { ArrowLeft, TrendingDown, Users, Activity } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import WeKitLogo from "@/components/WeKitLogo";

type FunnelRow = { step_index: number; step_name: string; users: number };
type FunnelRowEnriched = FunnelRow & {
  drop_from_prev_pct: number;
  conversion_from_top_pct: number;
};

const WINDOWS = [
  { value: "7", label: "Last 7 days" },
  { value: "30", label: "Last 30 days" },
  { value: "90", label: "Last 90 days" },
];

function enrich(rows: FunnelRow[]): FunnelRowEnriched[] {
  if (!rows.length) return [];
  const top = rows[0].users || 0;
  return rows.map((r, i) => {
    const prev = i === 0 ? r.users : rows[i - 1].users;
    const drop = prev === 0 ? 0 : ((prev - r.users) / prev) * 100;
    const conv = top === 0 ? 0 : (r.users / top) * 100;
    return {
      ...r,
      drop_from_prev_pct: Math.max(0, Math.round(drop)),
      conversion_from_top_pct: Math.round(conv),
    };
  });
}

export default function AdminAnalytics() {
  const [days, setDays] = useState("30");
  const [journey, setJourney] = useState<FunnelRow[]>([]);
  const [reflect, setReflect] = useState<FunnelRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([
      supabase.rpc("get_journey_funnel", { _days: parseInt(days, 10) }),
      supabase.rpc("get_reflect_funnel", { _days: parseInt(days, 10) }),
    ])
      .then(([j, r]) => {
        if (!active) return;
        if (j.error) setError(j.error.message);
        if (r.error) setError((prev) => prev ?? r.error?.message ?? null);
        setJourney((j.data as FunnelRow[] | null) ?? []);
        setReflect((r.data as FunnelRow[] | null) ?? []);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [days]);

  const journeyRows = useMemo(() => enrich(journey), [journey]);
  const reflectRows = useMemo(() => enrich(reflect), [reflect]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 border-b border-border/60 bg-background/85 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="sm" className="gap-1.5 -ml-2">
              <Link to="/dashboard">
                <ArrowLeft className="h-4 w-4" /> Dashboard
              </Link>
            </Button>
            <div className="hidden sm:block h-5 w-px bg-border" />
            <Link to="/" className="hidden sm:inline-flex">
              <WeKitLogo size="sm" />
            </Link>
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WINDOWS.map((w) => (
                <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 space-y-10">
        <section>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">
            Analytics · Funnels
          </h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">
            Distinct users that reached each step within the selected window. Drop-off
            shows where users abandoned the flow.
          </p>
        </section>

        {error && (
          <Card className="border-destructive/40 bg-destructive/5">
            <CardContent className="p-4 text-sm text-destructive">
              {error === "forbidden"
                ? "Admin role required."
                : `Failed to load funnels: ${error}`}
            </CardContent>
          </Card>
        )}

        <FunnelCard
          title="Journey funnel"
          subtitle="Viewed Journey → clicked a next-step CTA → opened a reflection → started steps → submitted."
          rows={journeyRows}
          loading={loading}
          testId="journey-funnel"
        />

        <FunnelCard
          title="Reflect step funnel"
          subtitle="Each step a user reached inside the monthly reflection flow."
          rows={reflectRows}
          loading={loading}
          testId="reflect-funnel"
        />
      </main>
    </div>
  );
}

function FunnelCard({
  title,
  subtitle,
  rows,
  loading,
  testId,
}: {
  title: string;
  subtitle: string;
  rows: FunnelRowEnriched[];
  loading: boolean;
  testId: string;
}) {
  const top = rows[0]?.users ?? 0;
  const bottom = rows.at(-1)?.users ?? 0;
  const overall = top === 0 ? 0 : Math.round((bottom / top) * 100);

  return (
    <Card data-testid={testId}>
      <CardHeader className="space-y-2">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="font-display text-xl">{title}</CardTitle>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Stat icon={Users} label="Top of funnel" value={top.toLocaleString()} />
            <Stat icon={Activity} label="Completed" value={bottom.toLocaleString()} />
            <Stat icon={TrendingDown} label="End-to-end" value={`${overall}%`} />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="h-64">
          {loading ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
              No events in this window yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={rows} margin={{ top: 10, right: 20, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="step_name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "hsl(var(--background))",
                    border: "1px solid hsl(var(--border))",
                    borderRadius: 8,
                    fontSize: 12,
                  }}
                  formatter={(v: number, name: string, props) => [
                    `${v} users (${props.payload.conversion_from_top_pct}% of top)`,
                    "Users",
                  ]}
                />
                <Bar dataKey="users" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="users" position="top" fill="hsl(var(--foreground))" fontSize={11} />
                  {rows.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "hsl(var(--primary))" : "hsl(var(--accent))"} fillOpacity={1 - i * 0.06} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm" data-testid={`${testId}-table`}>
            <thead className="text-[11px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left font-semibold px-2 py-2">Step</th>
                <th className="text-right font-semibold px-2 py-2">Users</th>
                <th className="text-right font-semibold px-2 py-2">From top</th>
                <th className="text-right font-semibold px-2 py-2">Drop vs prev</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {rows.map((r) => (
                <tr key={r.step_index} data-step={r.step_name}>
                  <td className="px-2 py-2 font-medium text-foreground">{r.step_index}. {r.step_name}</td>
                  <td className="px-2 py-2 text-right tabular-nums">{r.users.toLocaleString()}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-muted-foreground">{r.conversion_from_top_pct}%</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${r.drop_from_prev_pct > 30 ? "text-destructive" : "text-muted-foreground"}`}>
                    {r.step_index === 0 ? "—" : `${r.drop_from_prev_pct}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: string }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border/60 px-3 py-2 bg-card/60">
      <Icon className="h-3.5 w-3.5 text-muted-foreground" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="font-display font-semibold text-foreground">{value}</div>
      </div>
    </div>
  );
}
